// Who is currently waiting on a JS chunk, and therefore whether a failed chunk
// fetch should reload the page.
//
// This is the one piece of shared state behind the stale-deploy reload, and it
// lives in its own module because three unrelated places need it and none of
// them is "lazy with retry":
//
//   - modules/Common/utils/lazyWithRetry.js  increments the route counter
//   - modules/App/config/routeChunks.js      wraps background preloads
//   - src/index.jsx                          decides whether to reload
//
// A reader asking "why did the app reload under me?" should be able to find the
// answer by name. See docs/features/react-lazy-loading/phase-7-navigation-speed.md.

// Imports a user is actually waiting on: a lazy route mounting.
let pendingRouteImports = 0;
// Imports that are only a guess about the next click.
let pendingPreloads = 0;

/**
 * Marks a promise as a background preload for as long as it is in flight, so a
 * failure while only guesses are pending does not reload the page under a user
 * who is mid-task.
 *
 * @template T
 * @param {Promise<T>} promise
 * @returns {Promise<T>} the same promise, for chaining
 */
export const trackPreload = (promise) => {
  pendingPreloads += 1;
  return promise.finally(() => {
    pendingPreloads -= 1;
  });
};

/** Runs `fn` with the route-import counter held up for as long as it is pending. */
export const trackRouteImport = (fn) => {
  pendingRouteImports += 1;
  return fn().finally(() => {
    pendingRouteImports -= 1;
  });
};

/**
 * True when a `vite:preloadError` deserves a page reload.
 *
 * Reload while a user is waiting on a route chunk (`pendingRouteImports > 0`),
 * or when nothing is in flight at all (`pendingPreloads === 0`) — that second
 * case covers a plain `React.lazy` or `import()` outside this helper, which is
 * still a user waiting. Skip only when the sole imports in flight are
 * background guesses.
 */
export const shouldReloadOnPreloadError = () => pendingRouteImports > 0 || pendingPreloads === 0;
